import {
  createFileRoute,
  Link,
  useNavigate,
  useRouteContext,
  useSearch,
} from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useMemo, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import Plus from "@repo/icons/default/Plus";

import { RecurrenceLimitCard } from "@/components/recurring-appointment/recurrence-limit-card";
import { RecurrenceTabs } from "@/components/recurring-appointment/recurrence-tabs";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Fab } from "@/components/ui/fab/fab";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { recurringFlowOriginIndexAtom } from "@/lib/atoms/recurring-appointment-form-atom";
import { getHistoryIndex } from "@/lib/helpers/history";
import {
  classifyAppointmentStatus,
  classifyRecurrenceStatus,
  type RecurrenceListItemData,
} from "@/lib/helpers/recurrence";
import { trackEvent } from "@/lib/tracking";

const RecurringAppointmentsPageQuery = graphql(/* GraphQL */ `
  query RecurringAppointmentsPageQuery {
    utilizacaoRecorrencia {
      ...RecurrenceLimitCardFragment
    }
    agendamentosRecorrentes {
      codAgendamentoRecorrente
      diaDaSemana
      horaInicio
      horaFim
      frequenciaEmDias
      quantidadeAgendamentos
      status
      codPaciente
      nomePaciente
      agendamentos {
        codAgendamento
        data
        horaInicio
        horaFim
        cancelado
        emUso
      }
    }
  }
`);

const RecurringAppointmentsPage = (): JSX.Element => {
  const queryResult = useGraphQL(RecurringAppointmentsPageQuery, {});
  const { flags, user } = useRouteContext({ from: "__root__" });
  const { from, origem } = useSearch({ strict: false }) as {
    from?: "create";
    origem?: number;
  };
  const navigate = useNavigate();
  const setFlowOriginIndex = useSetAtom(recurringFlowOriginIndexAtom);

  const { data } = queryResult;

  const canCreateRecurrence = useMemo(
    () => flags["exibir-agendamento-recorrente"],
    [flags],
  );

  const recurrences: RecurrenceListItemData[] = (
    data?.agendamentosRecorrentes ?? []
  ).flatMap((recurrence) =>
    recurrence
      ? [
          {
            codAgendamentoRecorrente: recurrence.codAgendamentoRecorrente,
            diaDaSemana: recurrence.diaDaSemana,
            horaInicio: recurrence.horaInicio,
            horaFim: recurrence.horaFim,
            frequenciaEmDias: recurrence.frequenciaEmDias,
            quantidadeAgendamentos: recurrence.quantidadeAgendamentos,
            agendamentosEmUso: recurrence.agendamentos.filter(
              (a) => classifyAppointmentStatus(a) === "agendado",
            ).length,
            status: recurrence.status,
            codPaciente: recurrence.codPaciente ?? null,
            nomePaciente: recurrence.nomePaciente ?? null,
          },
        ]
      : [],
  );

  const hasActiveRecurrence = recurrences.some(
    (recurrence) => classifyRecurrenceStatus(recurrence.status) === "emUso",
  );

  // Vindo do fluxo (from=create), reposiciona o histórico até a origem, deixando
  // as telas do fluxo à frente para o voltar não reentrar nelas.
  const handleBack =
    from === "create"
      ? () => {
          const currentIndex = getHistoryIndex();
          if (
            typeof origem === "number" &&
            currentIndex !== null &&
            origem < currentIndex
          ) {
            window.history.go(origem - currentIndex);
          } else {
            window.history.back();
          }
        }
      : undefined;

  const handleCreateRecurrence = (): void => {
    setFlowOriginIndex(getHistoryIndex());
    trackEvent("Empty State Criar Agendamento Recorrente Clicado", {
      codUsuario: user.codUsuario,
    });
    void navigate({ to: "/recurring-appointments/create" });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" onClick={handleBack} />
        <HeaderTitle title="Agendamentos recorrentes" align="center" />
      </HeaderRoot>
      <div className="flex flex-1 flex-col min-h-0 w-full mx-auto overflow-hidden md:max-w-[768px]">
        <AsyncDataWrapper {...queryResult}>
          <Page className="px-6 pt-0 pb-[calc(6rem+env(safe-area-inset-bottom))]">
            {data?.utilizacaoRecorrencia ? (
              <div className="pt-4 pb-4">
                <RecurrenceLimitCard utilization={data.utilizacaoRecorrencia} />
              </div>
            ) : null}
            <RecurrenceTabs
              recurrences={recurrences}
              onCreateRecurrence={
                canCreateRecurrence ? handleCreateRecurrence : undefined
              }
            />
          </Page>
        </AsyncDataWrapper>
        {canCreateRecurrence && hasActiveRecurrence ? (
          <Link
            to="/recurring-appointments/create"
            onClick={() => {
              setFlowOriginIndex(getHistoryIndex());
              trackEvent("Fab Agendamento Recorrente Clicado", {
                codUsuario: user.codUsuario,
              });
            }}
          >
            <Fab isPageWithNav={false}>
              <Plus size={32} className="fill-white" />
            </Fab>
          </Link>
        ) : null}
      </div>
    </>
  );
};

export const Route = createFileRoute("/recurring-appointments/")({
  component: RecurringAppointmentsPage,
  validateSearch: z.object({
    from: z.enum(["create"]).optional(),
    origem: z.coerce.number().int().optional(),
  }),
});
